import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import LottiePlayer from './LottiePlayer';

/**
 * Voice Mode Component (In-Flow, No Overlay).
 * Replaces BottomInput when active.
 * Ripple aligned to bottom-center of screen.
 */
export default function VoiceMode({
  onEnd, onSendVoice,
  transcript, partialTranscript,
  isSpeaking, isMuted, volume, vadScore = 0,
  onToggleMute, onClearTranscript,
}) {
  const silenceTimerRef = useRef(null);
  const lastSentRef = useRef('');
  const pendingTranscriptRef = useRef('');

  useEffect(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

    // Only consider sending when: we have text, user stopped speaking, no partial in progress
    if (!transcript || isSpeaking || partialTranscript) return;
    if (transcript === lastSentRef.current) return;

    // Capture the transcript now in case it changes before timer fires
    pendingTranscriptRef.current = transcript;

    silenceTimerRef.current = setTimeout(() => {
      const toSend = pendingTranscriptRef.current;
      if (toSend && toSend !== lastSentRef.current) {
        lastSentRef.current = toSend;
        onSendVoice(toSend);
        if (onClearTranscript) onClearTranscript();
      }
    }, 1000); // 1s after silence is enough

    return () => { if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current); };
  }, [transcript, isSpeaking, partialTranscript, onSendVoice, onClearTranscript]);

  const displayText = partialTranscript
    ? (transcript ? transcript + ' ' + partialTranscript : partialTranscript)
    : transcript;
  const words = displayText ? displayText.split(/\s+/).filter(Boolean) : [];

  const activity = Math.max(vadScore, volume);
  const rippleSpeed = isSpeaking ? 0.9 + activity * 2.6 : 0.45;
  const rippleOpacity = isSpeaking ? 0.95 : 0.52;

  return (
    <motion.div
      data-testid="voice-mode"
      style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: 360,
        zIndex: 20,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end',
        overflow: 'visible',
        pointerEvents: 'none', // Allow clicks through to controls
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Transcript text replaces input area and stays in flow */}
      <div style={{
        position: 'absolute',
        left: 16,
        right: 16,
        bottom: 214,
        textAlign: 'center',
        zIndex: 25,
        minHeight: 72,
      }}>
        {displayText ? (
          <p
            style={{
              fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 500,
              color: 'rgba(255,255,255,0.95)', lineHeight: 1.36, margin: 0,
              textShadow: '0 2px 10px rgba(0,0,0,0.5)'
            }}
          >
            {words.map((word, idx) => (
              <motion.span
                key={`${word}-${idx}-${displayText}`}
                initial={{ opacity: 0, y: 5, filter: 'blur(2px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.22, delay: idx * 0.05 }}
                style={{ display: 'inline-block', marginRight: 6 }}
              >
                {word}
              </motion.span>
            ))}
          </p>
        ) : (
          <p style={{
            fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 500,
            color: 'rgba(255,255,255,0.5)', margin: 0
          }}>
            Listening...
          </p>
        )}
      </div>

      {/* Ripple animation stays aligned to mode-toggle midpoint (center X) */}
      <div style={{
        position: 'absolute',
        bottom: -58,
        left: 'calc(50% + 96px)',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 8400,
        height: 3920,
        display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
        zIndex: 21,
      }}>
        <motion.div
          style={{ width: 8400, maxWidth: '1680vw', height: 3920, marginBottom: -1848 }}
          animate={{
            scale: isSpeaking ? [1, 1.1 + activity * 0.42, 1] : 1,
            opacity: rippleOpacity,
          }}
          transition={{ duration: isSpeaking ? 0.16 : 0.4 }}
        >
          <LottiePlayer
            src="/animations/ripple.json"
            loop
            autoplay
            speed={rippleSpeed}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: '100%' }}
          />
        </motion.div>
      </div>

      {/* Controls Row (Mute/End) */}
      <div style={{
        position: 'absolute', bottom: 24, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', gap: 32,
        zIndex: 30, pointerEvents: 'auto',
      }}>
        <motion.button onClick={onToggleMute}
          style={{
            width: 50, height: 50, borderRadius: '50%',
            background: isMuted ? 'rgba(255,60,60,0.2)' : 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'white', backdropFilter: 'blur(10px)'
          }}
          whileTap={{ scale: 0.9 }}
        >
           {/* Mute Icon */}
           <div style={{width: 20, height: 20, background: 'white'}} />
        </motion.button>
        <motion.button onClick={onEnd}
          style={{
            width: 60, height: 60, borderRadius: '50%',
            background: 'rgba(255,60,60,0.3)', border: '1px solid rgba(255,60,60,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'white', backdropFilter: 'blur(10px)'
          }}
          whileTap={{ scale: 0.9 }}
        >
          {/* Close Icon */}
          <div style={{width: 24, height: 24, background: 'white'}} />
        </motion.button>
      </div>
    </motion.div>
  );
}
